/**
 * Check if the code is running in a browser environment.
 */
export function isBrowser() {
  return typeof window !== 'undefined';
}

export function isProduction() {
  return process.env.NODE_ENV === 'production';
}

export function isDevelopment() {
  return process.env.NODE_ENV === 'production';
}

type CurrencyParams = {
  currencyCode?: Intl.NumberFormatOptions['currency'];
  locale?: Intl.LocalesArgument;
  value: string | number;
};

/**
 * @name formatCurrency
 * @description Format the currency based on the currency code
 */
export function formatCurrency(params: CurrencyParams) {
  return new Intl.NumberFormat(params.locale ?? navigator.language, {
    style: 'currency',
    currency: params.currencyCode ?? 'EUR',
  }).format(Number(params.value));
}

export function formatBytes(bytes: number) {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let index = 0;
  let value = bytes;

  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index++;
  }

  return `${value.toFixed(2)} ${units[index]}`;
}

export function formatSpeed(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i] + '/s'
  );
}

export const formatTimeLeft = (seconds: number): string => {
  if (!isFinite(seconds) || seconds <= 0) {
    return 'calculating...';
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isEqual(
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>,
): boolean {
  if (obj1 === obj2) return true;
  if (
    typeof obj1 !== 'object' ||
    typeof obj2 !== 'object' ||
    obj1 === null ||
    obj2 === null
  )
    return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (
      !keys2.includes(key) ||
      !isEqual(
        obj1[key] as Record<string, unknown>,
        obj2[key] as Record<string, unknown>,
      )
    )
      return false;
  }
  return true;
}

export function isObject(item: unknown): item is Record<PropertyKey, unknown> {
  return !!item && typeof item === 'object' && !Array.isArray(item);
}

export function merge(target: unknown, ...sources: unknown[]) {
  if (!sources.length) return target;
  const source = sources.shift();
  if (isObject(target) && isObject(source)) {
    for (const key in source as Record<string, unknown>) {
      if (isObject((source as Record<string, unknown>)[key])) {
        if (!(target as Record<string, unknown>)[key]) {
          Object.assign(target as Record<string, unknown>, { [key]: {} });
        }
        merge(
          (target as Record<string, unknown>)[key],
          (source as Record<string, unknown>)[key],
        );
      } else {
        Object.assign(target as Record<string, unknown>, {
          [key]: (source as Record<string, unknown>)[key],
        });
      }
    }
  }
  return merge(target, ...sources);
}
/**
 * Takes in a function, wraps in in a try catch and returns a {data, error} object similar to all supabase functions.
 */
export const tryCatch = async <T>(
  fn: () => Promise<T>,
): Promise<
  { ok: true; data: T; error: never } | { ok: false; error: Error; data: never }
> => {
  try {
    const data = await fn();
    return { ok: true, data, error: undefined as never };
  } catch (error) {
    const e =
      error instanceof Error ? error : new Error(undefined, { cause: error });
    return { ok: false, error: e, data: undefined as never };
  }
};
